import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
	bookingOptionsForJourney,
	journeyEndBookingHeadline,
} from '../../data/bookingExperienceData';
import BookingTrustStrip from './BookingTrustStrip';
import '../../styles/booking-experience.scss';

const JOURNEY_END_ID = 'journey-end-booking';
const SCROLL_DEPTH_MIN = 0.52;

function bookingLinkForOption(opt, journey) {
	if (opt.id === 'group' && journey?.slug) {
		return `/group-trips?journey=${encodeURIComponent(journey.slug)}`;
	}
	if (opt.id === 'private' && journey?.slug) {
		return `/packages?journey=${encodeURIComponent(journey.slug)}`;
	}
	return opt.route;
}

/**
 * End-of-page booking CTA for long journey guides.
 * A compact sticky bar appears only after the reader has scrolled deep into the page;
 * the full panel sits at the very bottom and pins when reached.
 */
function JourneyBookingSection({ journey }) {
	const options = bookingOptionsForJourney(journey);
	const { lead, question } = journeyEndBookingHeadline(journey);
	const sectionRef = useRef(null);
	const [showStickyBar, setShowStickyBar] = useState(false);
	const [sectionInView, setSectionInView] = useState(false);

	useEffect(() => {
		const section = sectionRef.current;
		if (!section) return undefined;

		const observer = new IntersectionObserver(
			([entry]) => setSectionInView(entry.isIntersecting),
			{ threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
		);
		observer.observe(section);

		function onScroll() {
			const doc = document.documentElement;
			const scrollDepth = (window.scrollY + window.innerHeight) / doc.scrollHeight;
			setShowStickyBar(scrollDepth >= SCROLL_DEPTH_MIN);
		}

		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => {
			observer.disconnect();
			window.removeEventListener('scroll', onScroll);
		};
	}, []);

	const stickyVisible = showStickyBar && !sectionInView;

	return (
		<>
			{stickyVisible && (
				<div className="journey-booking-sticky" role="complementary" aria-label="Ready to book">
					<div className="journey-booking-sticky__inner container">
						<p className="journey-booking-sticky__text">{question}</p>
						<div className="journey-booking-sticky__actions">
							{options.map((opt) => (
								<Link key={opt.id} to={bookingLinkForOption(opt, journey)} className="journey-booking-sticky__btn">
									{opt.emoji && <span aria-hidden="true">{opt.emoji}</span>}
									<span>{opt.title}</span>
								</Link>
							))}
						</div>
					</div>
				</div>
			)}

			<section
				ref={sectionRef}
				id={JOURNEY_END_ID}
				className="journey-booking"
				aria-labelledby="journey-booking-title"
			>
				<div className="journey-booking__sticky-panel">
					<div className="journey-booking__inner">
						<p className="journey-booking__finished">{lead}</p>
						<h2 id="journey-booking-title" className="journey-booking__title">
							{question}
						</h2>
						<p className="journey-booking__note">
							Let VisitAllNepal handle the logistics — book with us and we assign the best verified local operator for your trip.
						</p>
						<div className="journey-booking__actions">
							{options.map((opt) => (
								<Link key={opt.id} to={bookingLinkForOption(opt, journey)} className="journey-booking__action">
									{opt.emoji && <span className="journey-booking__action-emoji" aria-hidden="true">{opt.emoji}</span>}
									<span className="journey-booking__action-label">{opt.title}</span>
									<span className="journey-booking__action-hint">{opt.bestFor}</span>
								</Link>
							))}
						</div>
						<BookingTrustStrip />
					</div>
				</div>
			</section>
		</>
	);
}

export default JourneyBookingSection;
